/**
 * Direct HTTP request templater (ENG-4926).
 *
 * Pure: takes a tool descriptor + validated args + a base URL + a token
 * + a request id (for idempotency) → returns a fully-formed { url,
 * method, headers, body } ready to hand to fetch.
 *
 * Templating rules:
 *   - `path_template` and `query_template` substitute `{key}` placeholders
 *     with `String(args[key])` after URL-encoding. Path placeholders that
 *     correspond to non-scalar args throw.
 *   - `headers_template` substitutes `{key}` placeholders the same way
 *     (CS-1517). A header whose template resolves to an empty
 *     string is DROPPED, not sent blank — that is what makes an optional
 *     scoping header (origami's `x-origami-project`) disappear when the
 *     caller omits the arg, instead of failing closed at the vendor.
 *     It may not target `Authorization`, `Accept`, `Content-Type` or the
 *     idempotency-key header (case-insensitive) — those are the templater's
 *     own, and a templated value comes from caller args.
 *   - `body_template` is one of:
 *       - `undefined` → no body sent
 *       - the literal string `"{$body}"` → JSON-encode the entire `args`
 *         object MINUS any key consumed solely by `headers_template`
 *         (a transport-scoping arg must not leak into the vendor's body).
 *         Path/query placeholder keys are still included — several vendors
 *         expect the path id echoed in the body, and changing that now
 *         would silently alter every existing `{$body}` tool.
 *       - any other JSON value → recursively substituted: each string
 *         leaf with `{key}` placeholders is rewritten; other leaves
 *         pass through unchanged. An object property whose value is exactly
 *         `{?key}` passes the arg through and is OMITTED when it is absent or
 *         null (ENG-10608), for explicit field allowlists.
 *   - Idempotency: a header is added with name `idempotency_key_header`
 *     (default `Idempotency-Key`) and value `request_id` for any non-GET
 *     method.
 *   - Auth: `Authorization: <scheme> <token>`, where `<scheme>` defaults to
 *     `Bearer` and is overridden per integration via `authScheme`
 *     (ENG-8440 — Higgsfield authenticates as `Authorization: Key
 *     KEY_ID:KEY_SECRET`). Only the scheme word varies; the header name and
 *     the token's position are fixed.
 *   - Basic auth (ENG-10524): `authEncoding: 'basic_username'` sends the
 *     stored credential as the HTTP Basic USERNAME with an empty password —
 *     `Authorization: Basic base64(<credential>:)` — which is how Sportsyear
 *     authenticates an API key. The raw key is what is stored; the encoding
 *     happens here, so the probe, rotation and redaction all keep seeing the
 *     key itself rather than a derived blob. It cannot be combined with
 *     `authScheme` (the scheme word is `Basic` by definition). Vendors needing
 *     a different header name are still a follow-up.
 */

import type { ToolDescriptor } from '../../types/integration-metadata.js';

export interface BuildHttpRequestArgs {
  tool: ToolDescriptor;
  /** Args validated against tool.input_schema. */
  args: Record<string, unknown>;
  /** Vendor base URL, e.g. `https://api.xero.com`. */
  baseUrl: string;
  /**
   * OAuth bearer token (or API key — caller decides what to put here).
   *
   * Optional (ADR 0010): when omitted/empty the templater emits no
   * `Authorization` header. Keyless Direct HTTP tools (e.g. Augmented Live publishing) execute
   * without a credential. The decision
   * to allow no credential is the *caller's* (the broker resolves mode
   * from scope + credential presence and fails closed when an
   * account-scoped tool has no credential).
   */
  bearerToken?: string;
  /**
   * Authorization scheme word placed before the token (ENG-8440).
   *
   * Defaults to `Bearer`. Higgsfield's REST API expects
   * `Authorization: Key KEY_ID:KEY_SECRET`, so the scheme is a per-integration
   * knob (`metadata.auth_scheme`) rather than a constant. Must be a single
   * RFC 7230 token — a value with whitespace or control characters throws,
   * because it is concatenated straight into a header value.
   *
   * Ignored entirely when `bearerToken` is absent: a keyless tool emits no
   * `Authorization` header at all, scheme or not.
   */
  authScheme?: string;
  /**
   * How the credential is encoded into `Authorization` (ENG-10524).
   *
   * Omitted → `<scheme> <token>` as above. `'basic_username'` → HTTP Basic
   * with the credential as the username and an empty password. Any other
   * value throws rather than falling back: the value is DB content
   * (`metadata.auth_encoding`), and a silent fallback to Bearer would 401 on
   * every call while looking exactly like a revoked key.
   *
   * Ignored when `bearerToken` is absent, like `authScheme`.
   */
  authEncoding?: string;
  /** approval_request.id, used as the Idempotency-Key value. */
  requestId: string;
  /** Extra headers (e.g. `Xero-Tenant-Id`). Merged after the templater's defaults; caller wins. */
  extraHeaders?: Record<string, string>;
}

export interface PreparedHttpRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
}

const PLACEHOLDER_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export function buildHttpRequest(input: BuildHttpRequestArgs): PreparedHttpRequest {
  const { tool, args, baseUrl, bearerToken, authScheme, authEncoding, requestId, extraHeaders } = input;
  const {
    method,
    path_template,
    query_template,
    headers_template,
    body_template,
    idempotency_key_header,
  } = tool.http;

  // ENG-10608: `{?key}` is a body-template form only. In a path or query
  // template it matches no placeholder and would reach the vendor literally.
  rejectOptionalPlaceholder('path_template', path_template);
  for (const v of Object.values(query_template ?? {})) rejectOptionalPlaceholder('query_template', v);
  const path = templatePath(path_template, args);
  const query = query_template ? buildQueryString(query_template, args) : '';
  const url = joinUrl(baseUrl, path) + query;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (bearerToken) {
    headers['Authorization'] = authorizationValue(bearerToken, authScheme, authEncoding);
  }
  if (method !== 'GET') {
    const headerName = idempotency_key_header ?? 'Idempotency-Key';
    headers[headerName] = requestId;
  }
  if (headers_template) {
    // The idempotency key is per-tool configurable, so the reserved set has to
    // be computed per call rather than being a module constant.
    const reserved = new Set([
      ...RESERVED_HEADER_NAMES,
      (idempotency_key_header ?? 'Idempotency-Key').toLowerCase(),
    ]);
    Object.assign(headers, buildTemplatedHeaders(headers_template, args, reserved));
  }
  let body: string | undefined;
  if (method !== 'GET' && body_template !== undefined) {
    // Keys consumed ONLY by a header template are transport concerns, not
    // payload — strip them before a `{$body}` passthrough so a scoping arg
    // never reaches the vendor as a body field it would reject.
    const bodyArgs =
      body_template === '{$body}' && headers_template
        ? omitKeys(args, headerOnlyPlaceholders(headers_template, tool.http))
        : args;
    const expanded = expandBodyTemplate(body_template, bodyArgs);
    body = JSON.stringify(expanded);
    headers['Content-Type'] = 'application/json';
  }
  if (extraHeaders) {
    Object.assign(headers, extraHeaders);
  }
  return { url, method, headers, body };
}

/**
 * Headers a `headers_template` may never write, matched case-insensitively.
 *
 * A templated value is expanded from tool-call `args`, so letting a descriptor
 * point one of these at an arg would put a caller-supplied string where the
 * broker's own credential / content negotiation lives. The `Idempotency-Key`
 * name is added per call (it is per-tool configurable) — see buildHttpRequest.
 *
 * Identity headers (`X-Organization-Id`, `Xero-Tenant-Id`, …) are NOT listed
 * because they are already safe by construction: `extraHeaders` is merged last
 * and wins, so a templated header cannot displace one. This set covers what
 * the templater itself sets.
 */
const RESERVED_HEADER_NAMES: ReadonlySet<string> = new Set(['authorization', 'accept', 'content-type']);

/**
 * Expand a `headers_template` against args. A header whose template resolves
 * to an empty string is omitted entirely (the optional-scoping-header case).
 * Non-scalar args throw, matching path/query behaviour.
 */
function buildTemplatedHeaders(
  template: Record<string, string>,
  args: Record<string, unknown>,
  reservedNames: ReadonlySet<string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [headerName, valueTemplate] of Object.entries(template)) {
    if (!isValidHeaderName(headerName)) {
      throw new TemplateError(`invalid header name in headers_template: ${headerName}`);
    }
    if (reservedNames.has(headerName.toLowerCase())) {
      throw new TemplateError(`headers_template cannot target reserved header: ${headerName}`);
    }
    const expanded = valueTemplate.replace(PLACEHOLDER_RE, (_full, key: string) => {
      const v = args[key];
      if (v === undefined || v === null) return '';
      if (!isScalar(v)) {
        throw new TemplateError(
          `header placeholder {${key}} cannot be substituted with non-scalar value`,
        );
      }
      return String(v);
    });
    if (expanded === '') continue;
    if (!isValidHeaderValue(expanded)) {
      throw new TemplateError(`header ${headerName} resolved to an invalid value`);
    }
    out[headerName] = expanded;
  }
  return out;
}

/**
 * Placeholder keys referenced by `headers_template` and by nothing else in the
 * request line (path or query). Those are the only ones safe to drop from a
 * `{$body}` passthrough — a key that also feeds the path/query is presumed to
 * be real payload the vendor may expect echoed.
 */
function headerOnlyPlaceholders(
  headersTemplate: Record<string, string>,
  http: { path_template: string; query_template?: Record<string, string> },
): Set<string> {
  const elsewhere = new Set<string>([
    ...placeholderKeys(http.path_template),
    ...Object.values(http.query_template ?? {}).flatMap(placeholderKeys),
  ]);
  const out = new Set<string>();
  for (const key of Object.values(headersTemplate).flatMap(placeholderKeys)) {
    if (!elsewhere.has(key)) out.add(key);
  }
  return out;
}

function placeholderKeys(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER_RE)].map((m) => m[1] as string);
}

function omitKeys(args: Record<string, unknown>, keys: Set<string>): Record<string, unknown> {
  if (keys.size === 0) return args;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(args)) {
    if (!keys.has(k)) out[k] = v;
  }
  return out;
}

/**
 * Resolve the Authorization scheme word (ENG-8440), defaulting to `Bearer`.
 *
 * The value comes from `integration_definitions.metadata.auth_scheme` — DB
 * content, not a compile-time constant — and is concatenated directly into a
 * header value, so it is validated as a single RFC 7230 token rather than
 * trusted. A blank/whitespace-only override falls back to `Bearer` instead of
 * emitting a malformed `Authorization:  <token>`.
 */
function authorizationValue(
  token: string,
  scheme: string | undefined,
  encoding: string | undefined,
): string {
  if (encoding === undefined || encoding.trim() === '') {
    return `${resolveAuthScheme(scheme)} ${token}`;
  }
  const trimmed = encoding.trim();
  if (trimmed !== 'basic_username') {
    throw new TemplateError(`unsupported auth_encoding: ${trimmed}`);
  }
  if (scheme !== undefined && scheme.trim() !== '') {
    // Two declarations of the scheme word, one of which must lose. Refusing is
    // cheaper than choosing: either answer is a 401 on a good credential for
    // whichever integration author meant the other one.
    throw new TemplateError('auth_scheme cannot be combined with auth_encoding');
  }
  return basicUsernameAuthorization(token);
}

/**
 * `Basic base64(<credential>:)` — the credential as the HTTP Basic username
 * with an empty password (ENG-10524, Sportsyear).
 *
 * Exported so the connectivity probe encodes the header with the SAME function
 * the broker uses. A probe that built its own would be a second implementation
 * of the one thing the probe exists to verify, and could read green on a
 * header the broker never sends.
 *
 * A credential containing `:` is refused: RFC 7617 forbids a colon in the
 * user-id, and the vendor would split on it and see a different key and a
 * non-empty password — a 401 indistinguishable from a revoked key. The error
 * message never includes the credential.
 */
export function basicUsernameAuthorization(credential: string): string {
  if (credential.includes(':')) {
    throw new TemplateError('credential for basic_username auth must not contain ":"');
  }
  if (!isValidHeaderValue(credential)) {
    throw new TemplateError('credential for basic_username auth contains control characters');
  }
  const bytes = new TextEncoder().encode(`${credential}:`);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return `Basic ${btoa(binary)}`;
}

function resolveAuthScheme(scheme: string | undefined): string {
  if (scheme === undefined) return 'Bearer';
  const trimmed = scheme.trim();
  if (trimmed === '') return 'Bearer';
  if (!isValidHeaderName(trimmed)) {
    throw new TemplateError(`invalid auth_scheme: ${trimmed}`);
  }
  return trimmed;
}

/** RFC 7230 token characters. */
function isValidHeaderName(name: string): boolean {
  return /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/.test(name);
}

/** Reject CR/LF (header injection) and other control characters. */
function isValidHeaderValue(value: string): boolean {
  // eslint-disable-next-line no-control-regex
  return !/[\x00-\x1f\x7f]/.test(value);
}

function templatePath(template: string, args: Record<string, unknown>): string {
  return template.replace(PLACEHOLDER_RE, (_full, key: string) => {
    const value = args[key];
    if (!isScalar(value)) {
      throw new TemplateError(`path placeholder {${key}} cannot be substituted with non-scalar value`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildQueryString(
  template: Record<string, string>,
  args: Record<string, unknown>,
): string {
  const params: string[] = [];
  for (const [paramName, valueTemplate] of Object.entries(template)) {
    const expanded = valueTemplate.replace(PLACEHOLDER_RE, (_full, key: string) => {
      const v = args[key];
      if (v === undefined || v === null) return '';
      if (!isScalar(v)) {
        throw new TemplateError(`query placeholder {${key}} cannot be substituted with non-scalar value`);
      }
      return String(v);
    });
    if (expanded !== '') {
      params.push(`${encodeURIComponent(paramName)}=${encodeURIComponent(expanded)}`);
    }
  }
  return params.length === 0 ? '' : `?${params.join('&')}`;
}

const OPTIONAL_PLACEHOLDER_RE = /\{\?([a-zA-Z_][a-zA-Z0-9_]*)\}/;

function rejectOptionalPlaceholder(field: string, template: unknown): void {
  if (typeof template === 'string' && OPTIONAL_PLACEHOLDER_RE.test(template)) {
    throw new TemplateError(
      `optional placeholder ${template.match(OPTIONAL_PLACEHOLDER_RE)![0]} is not supported in ${field} ` +
        `(only as the whole value of an object property in body_template)`,
    );
  }
}

function expandBodyTemplate(template: unknown, args: Record<string, unknown>): unknown {
  if (template === '{$body}') return args;
  if (typeof template === 'string') {
    // ENG-10608: `{?key}` has meaning ONLY as the entire value of an object
    // property in body_template (handled in the object branch, which never
    // reaches here for it). Anywhere else in the body (a top-level string, an
    // array element, embedded in text) it would match no placeholder and ship
    // the literal to the vendor, so it is refused. Path and query templates are
    // refused in `buildHttpRequest` for the same reason.
    if (OPTIONAL_PLACEHOLDER_RE.test(template)) {
      throw new TemplateError(
        `optional placeholder ${template.match(OPTIONAL_PLACEHOLDER_RE)![0]} is only valid as the whole value of an ` +
          `object property in body_template (got "${template}")`,
      );
    }
    return template.replace(PLACEHOLDER_RE, (_full, key: string) => {
      const v = args[key];
      if (v === undefined || v === null) return '';
      if (!isScalar(v)) {
        // Match templatePath / buildQueryString behaviour — non-scalar args
        // in a string-typed placeholder are a programming error, not a
        // value to silently stringify.
        throw new TemplateError(`body placeholder {${key}} cannot be substituted with non-scalar value`);
      }
      return String(v);
    });
  }
  if (Array.isArray(template)) {
    return template.map((item) => expandBodyTemplate(item, args));
  }
  if (template && typeof template === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(template as Record<string, unknown>)) {
      // ENG-10608: `{?key}` is the OPTIONAL whole-value placeholder. The arg
      // passes through when present and the FIELD IS OMITTED when it is absent
      // or null. Plain `{key}` falls through to string expansion when the arg
      // is absent and sends "", which a vendor can reject (an empty campaign
      // id) or act on. It is what lets a write tool list its allowed fields
      // explicitly instead of forwarding every arg with `{$body}` (ENG-10624).
      if (typeof v === 'string') {
        const optionalMatch = v.match(/^\{\?([a-zA-Z_][a-zA-Z0-9_]*)\}$/);
        if (optionalMatch && optionalMatch[1] !== undefined) {
          const value = args[optionalMatch[1]];
          if (value !== undefined && value !== null) out[k] = value;
          continue;
        }
      }
      // Object-valued placeholder: a string value of `{key}` substitutes
      // the entire arg in place, so structured args can pass through.
      if (typeof v === 'string') {
        const wholeMatch = v.match(/^\{([a-zA-Z_][a-zA-Z0-9_]*)\}$/);
        if (wholeMatch && wholeMatch[1] !== undefined && wholeMatch[1] in args) {
          out[k] = args[wholeMatch[1]];
          continue;
        }
      }
      out[k] = expandBodyTemplate(v, args);
    }
    return out;
  }
  return template;
}

function joinUrl(base: string, path: string): string {
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

export class TemplateError extends Error {
  readonly status = 400;
  constructor(message: string) {
    super(message);
    this.name = 'TemplateError';
  }
}
